import type { Gate } from '@/navigation/gates';
import { AREAS } from '@/navigation/areas';
import { permissionKeyFor } from '@/stores/entityStore';
import { CREATE_FORMS } from '@/components/createRecord/createRecordForms';

/**
 * What the Create New dialog offers, grouped the way Macro groups its list:
 * saved views first, then each record type under its sidebar group (Clients,
 * Music, ...), then Settings. Each option has one letter that picks it and
 * creates it straight away.
 *
 * A record type is offered only when it has a create form and the user holds
 * its create permission, the same permission the backend checks.
 */

export type CreateOptionId = 'saved-view' | 'import-view' | 'ddex-configuration' | 'new-player' | `record:${string}`;

export interface CreateOption {
    id: CreateOptionId;
    section: string;
    label: string;
    description: string;
    /** The single key that creates it while the dialog is open. Lower case. */
    shortcut: string;
    /** Null when everyone can use it. */
    gate: Gate | null;
    /** Only possible while a list is on screen. */
    needsList: boolean;
    /** For a record type: the entity whose form opens. */
    entityId?: string;
    /** The button's words when "Create <label>" would read badly. */
    actionLabel?: string;
    /** Never offered to a clients-only user, because the backend refuses it. */
    hiddenForClientsOnly?: boolean;
}

/** What the dialog's button says for an option. */
export function actionLabelFor(option: CreateOption): string {
    return option.actionLabel ?? `Create ${option.label.toLowerCase()}`;
}

/** Each record type's letter. V, I and X belong to the views and DDEX. */
const RECORD_SHORTCUTS: Readonly<Record<string, string>> = {
    Account: 'a',
    Location: 'l',
    LocationZone: 'z',
    Contact: 'o',
    Bucket: 'b',
    LocationZoneCustomPlaylist: 'u',
    ContactLoginCustomPlaylist: 'k',
};

const RECORD_DESCRIPTIONS: Readonly<Record<string, string>> = {
    Account: 'A new client account. Its locations and zones are added under it.',
    Location: 'A new location under an account.',
    LocationZone: 'A new zone in a location. Players are then moved into it.',
    Contact: 'A new contact. Link it to an account, location or zone on its page.',
    Bucket: 'A new bucket of tracks for one zone.',
    LocationZoneCustomPlaylist: 'A new custom playlist for one zone. It starts disabled.',
    ContactLoginCustomPlaylist: 'A new custom playlist for one contact login.',
};

const VIEW_OPTIONS: CreateOption[] = [
    {
        id: 'saved-view',
        section: 'Views',
        label: 'Saved view',
        description: 'Save the list you are looking at, with its filters, columns and sort, so you can open it again from the sidebar.',
        shortcut: 'v',
        gate: null,
        needsList: true,
    },
    {
        id: 'import-view',
        section: 'Views',
        label: 'Import a view',
        actionLabel: 'Import a view',
        description: 'Paste a share link a teammate sent you. You see the view before it is added to yours.',
        shortcut: 'i',
        gate: null,
        needsList: false,
    },
];

/**
 * A new player is not created: a player that is plugged in lands in the
 * internal new zone on its own, and "New player" moves it into its zone. So it
 * needs the player update permission, not create. A clients-only user cannot
 * reach the internal new zone, so it is never offered to them.
 */
const NEW_PLAYER_OPTION: CreateOption = {
    id: 'new-player',
    section: 'Players',
    label: 'Player',
    description: 'Move a player that was just plugged in out of the internal new zone and into the zone it belongs in. The player resets to pick up its new zone.',
    shortcut: 'p',
    gate: { kind: 'model', ability: 'update', permissionKey: 'location_zone_player' },
    needsList: false,
    entityId: 'LocationZonePlayer',
    actionLabel: 'Move a new player into a zone',
    hiddenForClientsOnly: true,
};

const RECORD_OPTIONS: CreateOption[] = AREAS.flatMap((area) => [
    ...(area.id === 'players' ? [NEW_PLAYER_OPTION] : []),
    ...area.entities
        .filter((entity) => CREATE_FORMS[entity.entityId] && RECORD_SHORTCUTS[entity.entityId])
        .map(
            (entity): CreateOption => ({
                id: `record:${entity.entityId}`,
                section: area.label,
                label: entity.noun,
                description: RECORD_DESCRIPTIONS[entity.entityId] ?? `A new ${entity.noun.toLowerCase()}.`,
                shortcut: RECORD_SHORTCUTS[entity.entityId],
                gate: { kind: 'model', ability: 'create', permissionKey: permissionKeyFor(entity.entityId) },
                needsList: false,
                entityId: entity.entityId,
                // A new account holds none of a clients-only user's zones, so the backend refuses it.
                hiddenForClientsOnly: entity.entityId === 'Account',
            }),
        ),
]);

export const CREATE_OPTIONS: readonly CreateOption[] = [
    ...VIEW_OPTIONS,
    ...RECORD_OPTIONS,
    {
        id: 'ddex-configuration',
        section: 'Settings',
        label: 'DDEX configuration',
        actionLabel: 'Add a DDEX configuration',
        description: 'Add a sender whose DDEX deliveries Tabasco should ingest. Opens the DDEX page at its add form.',
        shortcut: 'x',
        gate: { kind: 'developer' },
        needsList: false,
    },
];

export interface CreateSection {
    label: string;
    options: CreateOption[];
}

/** The options this user may see, in sections, in the order above. */
export function visibleCreateSections(gateIsOpen: (gate: Gate) => boolean, clientsOnly = false): CreateSection[] {
    const sections: CreateSection[] = [];
    for (const option of CREATE_OPTIONS) {
        if ((option.gate && !gateIsOpen(option.gate)) || (clientsOnly && option.hiddenForClientsOnly)) {
            continue;
        }
        const section = sections.find((candidate) => candidate.label === option.section);
        if (section) {
            section.options.push(option);
        } else {
            sections.push({ label: option.section, options: [option] });
        }
    }
    return sections;
}

/** Why an option cannot be created right now, or null when it can. */
export function unavailableReason(option: CreateOption, onEntityList: boolean): string | null {
    if (option.needsList && !onEntityList) {
        return 'Open a list first. A saved view is made from the list on screen.';
    }
    return null;
}

/** True when a key press came from somewhere the user is typing. */
export function isTypingTarget(target: EventTarget | null): boolean {
    if (!(target instanceof HTMLElement)) {
        return false;
    }
    return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}
